import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { acquireAssetObjectUrl, assetFetchKey } from "./assetFetch.js";

/**
 * Movable / resizable floating preview for PDFs and images fetched with auth.
 */
export default function FloatingAssetPreview({
    open,
    asset,
    sourceUrl,
    title = "Preview",
    contentType = "",
    onClose,
}) {
    const [blobUrl, setBlobUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [resolvedType, setResolvedType] = useState(contentType || "");

    const [pos, setPos] = useState({ x: 80, y: 72 });
    const [size, setSize] = useState({ w: 720, h: 820 });
    const dragRef = useRef(null);

    const fetchTarget = asset || sourceUrl;
    const key = assetFetchKey(fetchTarget);
    const typeHint = contentType || asset?.contentType || "";

    useEffect(() => {
        if (!open || !key) {
            setBlobUrl("");
            setError("");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        let release = () => {};

        async function load() {
            setLoading(true);
            setError("");
            try {
                const result = await acquireAssetObjectUrl(key, typeHint);
                if (cancelled) {
                    result.release();
                    return;
                }
                release = result.release;
                setResolvedType(result.contentType || typeHint || "");
                setBlobUrl(result.blobUrl);
            } catch (err) {
                if (cancelled) return;
                const msg =
                    err?.response?.data?.error ||
                    err?.message ||
                    "Failed to load file.";
                setError(typeof msg === "string" ? msg : "Failed to load file.");
                setBlobUrl("");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();

        return () => {
            cancelled = true;
            release();
        };
    }, [open, key, typeHint]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const onDragStart = useCallback(
        (e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const origin = { ...pos };

            const onMove = (ev) => {
                const maxX = Math.max(0, window.innerWidth - 120);
                const maxY = Math.max(0, window.innerHeight - 48);
                setPos({
                    x: Math.min(maxX, Math.max(0, origin.x + ev.clientX - startX)),
                    y: Math.min(maxY, Math.max(0, origin.y + ev.clientY - startY)),
                });
            };
            const onUp = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
            };
            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
        },
        [pos]
    );

    if (!open) return null;

    const isPdf =
        String(resolvedType).includes("pdf") ||
        String(key).toLowerCase().includes(".pdf") ||
        String(asset?.filename || "").toLowerCase().endsWith(".pdf");
    const isImage = String(resolvedType).startsWith("image/");

    return (
        <Box
            ref={dragRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            sx={{
                position: "fixed",
                left: pos.x,
                top: pos.y,
                width: size.w,
                height: size.h,
                zIndex: 1400,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
                border: "1px solid #d8dee6",
                borderRadius: "12px",
                boxShadow: "0 18px 48px rgba(15, 23, 42, 0.22)",
                overflow: "hidden",
                resize: "both",
                minWidth: 360,
                minHeight: 280,
                maxWidth: "96vw",
                maxHeight: "94vh",
            }}
            onMouseUp={() => {
                const el = dragRef.current;
                if (!el) return;
                const w = el.offsetWidth;
                const h = el.offsetHeight;
                if (w !== size.w || h !== size.h) setSize({ w, h });
            }}
        >
            <Box
                onPointerDown={onDragStart}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    backgroundColor: "#0f172a",
                    color: "#f8fafc",
                    cursor: "grab",
                    userSelect: "none",
                    flexShrink: 0,
                    "&:active": { cursor: "grabbing" },
                }}
            >
                <OpenInFullOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: 13,
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {title}
                </Typography>
                <IconButton
                    size="small"
                    onClick={onClose}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label="Close preview"
                    sx={{ color: "#f8fafc", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                >
                    <CloseOutlinedIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: "#eef2f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {loading && !blobUrl ? (
                    <CircularProgress size={28} />
                ) : error ? (
                    <Typography sx={{ px: 2, fontSize: 14, color: "#b91c1c", textAlign: "center" }}>
                        {error}
                    </Typography>
                ) : blobUrl && isImage ? (
                    <Box
                        component="img"
                        src={blobUrl}
                        alt={title}
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                        }}
                    />
                ) : blobUrl ? (
                    <Box
                        component="iframe"
                        title={title}
                        src={isPdf ? `${blobUrl}#toolbar=1` : blobUrl}
                        sx={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            backgroundColor: "#fff",
                        }}
                    />
                ) : null}
            </Box>
        </Box>
    );
}
